import React, { useState, useRef, useEffect } from 'react';
import SaveButtonWithModal from "./SaveButtonWithModel";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FiEye, FiEyeOff } from "react-icons/fi";

const person = [
  "Male", "Female", "Youth ","Physical ",
];

// Validation helpers
const isValidName = (name) => {
  if (!name) return false;
  const words = name.trim().split(/\s+/);
  return words.length <= 8 && /^[A-Za-z\s]+$/.test(name);
};
const isValidSurname = (surname) => {
  if (!surname) return false;
  const words = surname.trim().split(/\s+/);
  return words.length <= 8 && /^[A-Za-z\s]+$/.test(surname);
};
// Accept Date object or string in YYYY-MM-DD
const isValidDateOfBirth = (dob) => {
  if (!dob) return false;
  if (dob instanceof Date && !isNaN(dob)) return true;
  return /^\d{4}-\d{2}-\d{2}$/.test(dob);
};
const isValidId = (id) => {
  return id && /^[A-Za-z0-9]+$/.test(id) && id.length >= 5;
};
const isValidMobile = (mobile) => {
  return /^\+?[0-9\s\-]{7,20}$/.test(mobile);
};
const isValidEmail = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};
const isValidPassword = (password) => {
  return /^(?=.*[A-Za-z])(?=.*\d).{6,}$/.test(password);
};
const isValidWhatsapp = (whatsapp) => {
  return /^\+?[0-9\s\-]{7,20}$/.test(whatsapp);
};
const isValidPhysicalAddress = (address) => {
  return address && address.length >= 5 && address.length <= 100;
};
const isValidPostalCode = (code) => {
  return /^\d{4,6}$/.test(code);
};

const handleSelect = (option, formData, onFormDataChange) => {
  onFormDataChange({ ...formData, typeOfId: option });
};

const requiredFields = [
  "firstName", "surname", "dateOfBirth", "typeOfId", "idNumber",
  "mobile", "email", "password", "whatsapp", "physicalAddress", "postalCode"
];
const fieldValidators = {
  firstName: isValidName,
  surname: isValidSurname,
  dateOfBirth: isValidDateOfBirth,
  typeOfId: (val) => !!val && val.trim() !== "",
  idNumber: isValidId,
  mobile: isValidMobile,
  email: isValidEmail,
  password: isValidPassword,
  whatsapp: isValidWhatsapp,
  physicalAddress: isValidPhysicalAddress,
  postalCode: isValidPostalCode,
};

const fieldErrorMessages = {
  firstName: "First Name: max 8 words, only letters.",
  surname: "Surname: max 8 words, only letters.",
  dateOfBirth: "Date required (YYYY-MM-DD).",
  typeOfId: "Type of ID is required.",
  idNumber: "Alphanumeric, at least 5 characters.",
  mobile: "Enter a valid phone number (may start with +).",
  whatsapp: "Enter a valid WhatsApp number (may start with +).",
  email: "Invalid email address.",
  password: "Min 6 chars, 1 letter & 1 number.",
  physicalAddress: "5-100 characters.",
  postalCode: "4-6 digits.",
};
const CustomDateDiv = React.forwardRef(({ value, onClick }, ref) => (
  <div
    onClick={onClick}
    ref={ref}
    className="xl:w-[390px]  lg:w-[330px] md:w-[250px] w-[115px] h-[30px] md:h-[71px] p-1 md:p-2 pl-3 md:pl-4 text-[8px] md:text-[32px] text-[#979797] border rounded-[5px] bg-[#E7EBEB] border-[#E7EBEB] cursor-pointer"
  >
    {value || "YYYY-MM-DD"}
  </div>
));
const PersonalInfoForm = ({
  formData,
  onFormDataChange,
  race,
  onRaceChange,
  gender: selectedGender,
  onGenderChange,
  disability,
  onDisabilityChange,
  onSave
}) => {
  const [showModal, setShowModal] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  // Validate all fields on change and on submit
  const validateAllFields = (data = formData) => {
    let errors = {};
    requiredFields.forEach((field) => {
      const value = data[field];
      if (!value || (typeof value === "string" && value.trim() === "")) {
        errors[field] = "This field is required.";
      } else if (fieldValidators[field] && !fieldValidators[field](value)) {
        errors[field] = fieldErrorMessages[field];
      }
    });
    return errors;
  };

  // Real-time validation per field
  const handleChange = (e) => {
    const { name, value } = e.target;
    onFormDataChange({ ...formData, [name]: value });

    let error = "";
    if (!value || value.trim() === "") {
      error = "This field is required.";
    } else if (fieldValidators[name] && !fieldValidators[name](value)) {
      error = fieldErrorMessages[name];
    }
    setFieldErrors((prev) => ({ ...prev, [name]: error }));
  };

  // Date picker change handler
  const handleDateChange = (date) => {
    onFormDataChange({ ...formData, dateOfBirth: date });
    let error = "";
    if (!date) {
      error = "This field is required.";
    } else if (!isValidDateOfBirth(date)) {
      error = fieldErrorMessages.dateOfBirth;
    }
    setFieldErrors((prev) => ({ ...prev, dateOfBirth: error }));
  };
  const handleSelect = (option) => {
    onFormDataChange((prev) => ({ ...prev, typeOfId: option }));
    setIsOpen(false); // Close dropdown after selection
  };

  // Check for empty gender/disability
  const handleGenderChange = (val) => {
    onGenderChange(val);
    if (!val) {
      setFieldErrors((prev) => ({ ...prev, gender: "Please select gender." }));
    } else {
      setFieldErrors((prev) => {
        const { gender, ...rest } = prev;
        return rest;
      });
    }
  };
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownOptions, setDropdownOptions] = useState([
    "Physical Disability",
    "Visual Impairment",
    "Hearing Impairment",
    "Intellectual Disability",
    "Mental Health Condition"
  ]);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  const handleDisabilityChange = (val) => {
    onDisabilityChange(val);
    if (!val) {
      setFieldErrors((prev) => ({ ...prev, disability: "Please select disability/ability." }));
    } else {
      setFieldErrors((prev) => {
        const { disability, ...rest } = prev;
        return rest;
      });
    }
  };

  // On Save: validate all
  const handleSave = () => {
    const errors = validateAllFields();
    if (!selectedGender) errors.gender = "Please select gender.";
    if (!disability) errors.disability = "Please select disability/ability.";
    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      setSubmitError("Please fix the errors below.");
      return;
    }
    setSubmitError("");
    setShowModal(true);
    onSave({
      ...formData,
      race,
      gender: selectedGender,
      disability,
    });
  };
  const [isSelected, setSelected] = useState();

  return (
    <div className="mx-auto  md:mt-10 mt-5 ">
      <h2 className="text-center font-bold text-[10px] md:text-[32px] text-black">
      INDIVIDUAL INFORMATION
      </h2>
      <p className="text-center text-[8px] md:text-[32px] text-black md:mb-8 mb-3">
      Individual <span className="font-medium">Applications Only</span> 
      </p>
      {submitError && <div className="text-red-600 text-center mb-2 text-sm">{submitError}</div>}
      {/* FORM */}
      <form onSubmit={e => e.preventDefault()}>
        <div>
          <div className="flex flex-col md:gap-y-6 gap-2 w-full">
            {/* Row 1 */}
            <div className="flex md:flex-row  md:gap-2 gap-1">
              {/* First Name */}
              <div className="w-full">
                <label className="block font-bold text-[8px] md:text-[32px] px-2 text-black"><span className="text-red-500">*</span> Name of  Farmer</label>
                <input
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="XXXX"
                  required
                  className="w-full h-[30px] md:h-[71px] p-1 md:p-2 pl-3 md:pl-4 text-[8px] md:text-[32px] border rounded-[5px] text-black bg-[#E7EBEB] placeholder:text-[#979797] md:placeholder:text-[#979797] placeholder:text-[8px] md:placeholder:text-[32px] placeholder:text-black"
                />
                {fieldErrors.firstName && <div className="text-red-500 text-xs mt-1">{fieldErrors.firstName}</div>}
              </div>
              {/* Surname */}
              <div className="w-full">
                <label className="block font-bold text-[8px] md:text-[32px] px-2 text-black"><span className="text-red-500">*</span> Surname</label>
                <input
                  name="surname"
                  value={formData.surname}
                  onChange={handleChange}
                  placeholder="XXXX"
                  required
                  className="w-full h-[30px] md:h-[71px] p-1 md:p-2 pl-3 md:pl-4 text-[8px] md:text-[32px] border rounded-[5px] text-black bg-[#E7EBEB] placeholder:text-[#979797] md:placeholder:text-[#979797] placeholder:text-[8px] md:placeholder:text-[32px] placeholder:text-black"
                />
                {fieldErrors.surname && <div className="text-red-500 text-xs mt-1">{fieldErrors.surname}</div>}
              </div>
        
            </div>
            {/* Row 2 */}
            <div className="flex md:flex-row md:gap-2 gap-1">
              {/* Type of ID */}
              <div className="w-full relative">
                <div className="flex justify-between items-center cursor-pointer" onClick={() => setIsOpen(!isOpen)}>
                  <label className="block font-bold text-[8px] md:text-[32px] px-2 text-black"><span className="text-red-500">*</span> Identity Document</label>
                </div>

                <div
                  className="w-full h-[30px] md:h-[71px] p-1 md:p-2 pl-3 md:pl-4 text-[8px] md:text-[32px] border rounded-[5px] bg-[#E7EBEB] placeholder:text-[#979797] md:placeholder:text-[#979797] placeholder:text-[8px] md:placeholder:text-[32px] placeholder:text-black flex items-center justify-between cursor-pointer"
                  onClick={() => setIsOpen(!isOpen)}
                >
                  {formData.typeOfId || "Passport"}

                  <img
                    src="https://res.cloudinary.com/dzngpgki4/image/upload/v1746389452/Expand_down_19_ylkeiy.png"
                    alt="Dropdown icon"
                    className="md:w-[28px] md:h-[28px] w-[10px] h-[10px]"
                  />
                </div>

                {isOpen && (
                  <div className="absolute z-10 w-full bg-white border rounded-md text-black mt-1 max-h-60 overflow-auto shadow-lg">
                    {["Identity card", "Passport", "Drivers License "].map((option, index) => (
                      <div
                        key={index}
                        onClick={() => handleSelect(option, formData, onFormDataChange)}
                        className="px-3 py-2 text-[8px] md:text-[24px] hover:bg-[#E7EBEB] cursor-pointer"
                      >
                        {option}
                      </div>
                    ))}
                  </div>
                )}

                {fieldErrors.typeOfId && <div className="text-red-500 text-xs mt-1">{fieldErrors.typeOfId}</div>}
              </div>

              {/* ID Number */}
              <div className="w-full relative">
                <label className="block font-bold text-[8px] md:text-[32px] px-2 text-black"><span className="text-red-500">*</span> ID Number</label>
                <input
                  name="idNumber"
                  value={formData.idNumber}
                  onChange={handleChange}
                  required
                  placeholder="XXXXXXX"
                  className="w-full h-[30px] md:h-[71px] p-1 md:p-2 pl-3 md:pl-4 text-[8px] md:text-[32px] border rounded-[5px] text-black bg-[#E7EBEB] placeholder:text-[8px] md:placeholder:text-[32px] placeholder:text-[#979797] cursor-pointer"
                />
                {fieldErrors.idNumber && <div className="text-red-500 text-xs mt-1">{fieldErrors.idNumber}</div>}
              </div>
              {/* Mobile */}
    
            </div>
            {/* Row 3 */}
            <div className="flex md:flex-row md:gap-2 gap-1">
              {/* Email */}
       
           
              {/* WhatsApp */}
              <div className="w-full">
                <label className="block font-bold text-[8px] md:text-[32px] px-2 text-black"><span className="text-red-500">*</span> Mobile phone number</label>
                <input
                  name="whatsapp"
                  value={formData.whatsapp}
                  onChange={handleChange}
                  required
                  placeholder="XXXXXXXXXX"
                  className="w-full h-[30px] md:h-[71px] p-1 md:p-2 pl-3 md:pl-4 text-[8px] md:text-[32px] border rounded-[5px] text-black bg-[#E7EBEB] placeholder:text-[#979797] md:placeholder:text-[#979797] placeholder:text-[8px] md:placeholder:text-[32px] placeholder:text-black"
                />
                {fieldErrors.whatsapp && <div className="text-red-500 text-xs mt-1">{fieldErrors.whatsapp}</div>}
              </div>
              <div className="w-full">
                <label className="block font-bold text-[8px] md:text-[32px] px-2 text-black"><span className="text-red-500">*</span> WhatsApp number</label>
                <input
                  name="mobile"
                  type="text"
                  value={formData.mobile}
                  onChange={handleChange}
                  placeholder="0XXXXXXXXXXXX"
                  required
                  className="w-full h-[30px] md:h-[71px] p-1 md:p-2 pl-3 md:pl-4 text-[8px] md:text-[32px] border rounded-[5px] text-black bg-[#E7EBEB] border-[#E7EBEB] placeholder:text-[#979797] md:placeholder:text-[#979797] placeholder:text-[8px] md:placeholder:text-[32px] placeholder:text-black"
                />
                {fieldErrors.mobile && <div className="text-red-500 text-xs mt-1">{fieldErrors.mobile}</div>}
              </div>
         
            </div>
         <div className="flex md:flex-row md:gap-2 gap-1">
         <div className="w-full">
                <label className="block font-bold text-[8px] md:text-[32px] px-2 text-black"><span className="text-red-500">*</span> Email</label>
                <input
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="XXXXXX"
                  required
                  className="w-full h-[30px] md:h-[71px] p-1 md:p-2 pl-3 md:pl-4 text-[8px] md:text-[32px] border rounded-[5px] text-black bg-[#E7EBEB] placeholder:text-[#979797] md:placeholder:text-[#979797] placeholder:text-[8px] md:placeholder:text-[32px] placeholder:text-black"
                />
                {fieldErrors.email && <div className="text-red-500 text-xs mt-1">{fieldErrors.email}</div>}
              </div>
              <div className="w-full">
  <label className="block font-bold text-[8px] md:text-[32px] px-2 text-black"><span className="text-red-500">*</span> Password</label>
  <div className="relative">
    <input
      name="password"
      type={showPassword ? "text" : "password"}
      value={formData.password}
      onChange={handleChange}
      required
      placeholder="XXX1234"
      className="w-full h-[30px] md:h-[71px] p-1 md:p-2 pl-3 md:pl-4 text-[8px] md:text-[32px] border rounded-[5px] text-black border-[#E7EBEB] bg-[#E7EBEB] placeholder:text-[#979797] md:placeholder:text-[#979797] placeholder:text-[8px] md:placeholder:text-[32px] placeholder:text-black"
    />
    <button
      type="button"
      onClick={togglePasswordVisibility}
      className="absolute right-3 md:text-[30px] top-1/2 transform -translate-y-1/2 text-[#979797]"
    >
      {showPassword ? <FiEye /> : <FiEyeOff />}
    </button>
  </div>
  {fieldErrors.password && <div className="text-red-500 text-xs mt-1">{fieldErrors.password}</div>}
</div>
         </div>
            {/* Row 4 */}
            <div className="flex md:flex-row md:gap-2 gap-1">
              {/* Physical Address */}
              <div className="w-full">
                <label className="block font-bold text-[8px] md:text-[32px] px-2 text-black"><span className="text-red-500">*</span> Physical Address</label>
                <input
                  name="physicalAddress"
                  value={formData.physicalAddress}
                  onChange={handleChange}
                  required
                  placeholder="XXXXXX"
                  className="w-full h-[30px] md:h-[71px] p-1 md:p-2 pl-3 md:pl-4 text-[8px] md:text-[32px] border rounded-[5px] text-black bg-[#E7EBEB] placeholder:text-[#979797] md:placeholder:text-[#979797] placeholder:text-[8px] md:placeholder:text-[32px] placeholder:text-black"
                />
                {fieldErrors.physicalAddress && <div className="text-red-500 text-xs mt-1">{fieldErrors.physicalAddress}</div>}
              </div>
              {/* Postal Code */}
              <div className="w-full">
                <label className="block font-bold text-[8px] md:text-[32px] px-2 text-black"><span className="text-red-500">*</span> Postal Code</label>
                <input
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleChange}
                  required
                  placeholder="XXXXXX"
                  className="w-full h-[30px] md:h-[71px] p-1 md:p-2 pl-3 md:pl-4 text-[8px] md:text-[32px] border rounded-[5px] text-black bg-[#E7EBEB] placeholder:text-[#979797] md:placeholder:text-[#979797] placeholder:text-[8px] md:placeholder:text-[32px] placeholder:text-black"
                />
                {fieldErrors.postalCode && <div className="text-red-500 text-xs mt-1">{fieldErrors.postalCode}</div>}
              </div>
            </div>
          </div>
        </div>
      </form>
      {/* Race Section */}

      {/* Gender Section */}

      {/* Disability Section */}
      <div className="md:mt-8 mt-4">
  <label className="block font-bold md:mb-2 md:pl-2 mb-1 text-[6px] md:text-[32px] text-black">
    <span className="text-red-500">*</span> Person with Disability / Ability
  </label>
  <div className="grid grid-cols-2 md:grid-cols-2 gap-2">
  {person.map((p, i) => {
    const isLastItem = i === person.length - 1;
    const isActive = disability === p || (isLastItem && dropdownOptions.includes(disability));
    
    return (
      <button
        key={i}
        type="button"
        onClick={() => !isLastItem && handleDisabilityChange(p)}
        className={`flex items-center justify-between w-full h-[25px] md:h-[71px] bg-[#E7EBEB] border rounded px-4 ${
          isLastItem ? 'relative' : ''
        }`}
      >
        {/* Right section - only for last item (Disability text and dropdown) */}
        {isLastItem && (
          <div 
            className="flex items-center justify-between md:w-[550px] mr-8 border-l"
            onClick={(e) => {
              e.stopPropagation();
              setIsDropdownOpen(!isDropdownOpen);
            }}
          >
            <span className="font-medium text-[6px] md:text-[24px] text-black">
              {/* Show selected option if one is selected, otherwise "Disability" */}
              {dropdownOptions.includes(disability) ? disability : "Disability"}
            </span>
            <svg 
              width={window.innerWidth > 768 ? "24" : "12"} 
              height={window.innerWidth > 768 ? "24" : "12"} 
              viewBox="0 0 24 24" 
              fill="none" 
              className={`text-black transition-transform ${
                isDropdownOpen ? 'rotate-180' : ''
              }`}
            >
              <path 
                d="M6 9L12 15L18 9" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
          </div>
        )}
        
        {/* Left section - always shows "Disability" text and radio */}
        <div className="flex items-center justify-between w-full">
          <span className="font-medium text-[6px] md:text-[28px]">
            {p} {/* Always show the original text here */}
          </span>
          
          {/* Radio button */}
          <span
            className={`md:w-[55px] md:h-[55px] h-[22px] w-[22px] rounded-full ${
              isActive 
                ? 'bg-[#152C24] md:border-[7px] border-[4px] border-white' 
                : 'md:border-[8px] border-[4px] border-white bg-transparent'
            }`}
          />
        </div>

        {/* Dropdown menu - only for last item */}
        {isLastItem && isDropdownOpen && (
          <div 
            ref={dropdownRef}
            className="absolute top-full left-0 right-0 mt-1 z-10 md:w-[300px] bg-white border border-gray-200 rounded-md shadow-lg py-1"
          >
            {dropdownOptions.map((option, index) => (
              <div
                key={index}
                className="px-4 py-2 hover:bg-gray-100 flex justify-between  cursor-pointer text-[6px] md:text-[16px]"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDisabilityChange(option);
                  setIsDropdownOpen(false);
                }}
              >
                {option}
                <div
                      className={`w-2 h-2 md:w-4 md:h-4 rounded-full ${
                        isSelected ? "bg-[#152C24]" : "bg-gray-200"
                      }`}
                    ></div>
              </div>
            ))}
          </div>
        )}
      </button>
    );
  })}
</div>
  {fieldErrors.disability && <div className="text-red-500 text-xs mt-1">{fieldErrors.disability}</div>}
 
</div>
      {/* Save button */}
      <SaveButtonWithModal />
      {/* Save Modal */}
      {showModal && (
        <SaveButtonWithModal
          isOpen={showModal}
          onRequestClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
};

export default PersonalInfoForm;